import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Plus, Trash2, X, Calendar, Clock, Monitor } from 'lucide-react';
import { api } from '@/lib/api';
import type { CinemaShowing, CinemaScreen, CinemaFilm } from '@/types';

const showingSchema = z.object({
  filmId: z.string().min(1, 'Film is required'),
  screenId: z.string().min(1, 'Screen is required'),
  showDate: z.string().min(1, 'Date is required'),
  startTime: z.string().min(1, 'Start time is required'),
  endTime: z.string().optional(),
  basePrice: z.number().min(0, 'Price must be positive'),
  currency: z.string(),
});

type ShowingFormData = z.infer<typeof showingSchema>;

const bulkSchema = z.object({
  filmId: z.string().min(1, 'Film is required'),
  screenId: z.string().min(1, 'Screen is required'),
  basePrice: z.number().min(0, 'Price must be positive'),
});

type BulkFormData = z.infer<typeof bulkSchema>;

export default function ShowingsList() {
  const { venueId, filmId: routeFilmId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [filterScreenId, setFilterScreenId] = useState<string>('');
  const [bulkDates, setBulkDates] = useState<string[]>([]);
  const [bulkTimes, setBulkTimes] = useState<string[]>([]);

  const { data: films } = useQuery<CinemaFilm[]>({
    queryKey: ['cinema-films', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/cinema/films`);
      return response.data;
    },
  });

  const { data: screens } = useQuery<CinemaScreen[]>({
    queryKey: ['cinema-screens', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/cinema/screens`);
      return response.data;
    },
  });

  const { data: showings, isLoading } = useQuery<CinemaShowing[]>({
    queryKey: ['cinema-showings', venueId, selectedDate, filterScreenId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedDate) params.append('date', selectedDate);
      if (filterScreenId) params.append('screenId', filterScreenId);
      const response = await api.get(`/admin/venues/${venueId}/cinema/showings?${params}`);
      return response.data;
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ShowingFormData>({
    resolver: zodResolver(showingSchema),
    defaultValues: {
      filmId: routeFilmId || '',
      screenId: '',
      showDate: selectedDate,
      startTime: '',
      endTime: '',
      basePrice: 10,
      currency: 'GBP',
    },
  });

  const {
    register: registerBulk,
    handleSubmit: handleSubmitBulk,
    formState: { errors: bulkErrors },
    reset: resetBulk,
  } = useForm<BulkFormData>({
    resolver: zodResolver(bulkSchema),
    defaultValues: {
      filmId: routeFilmId || '',
      screenId: '',
      basePrice: 10,
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: ShowingFormData) => {
      await api.post(`/admin/venues/${venueId}/cinema/showings`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cinema-showings', venueId] });
      queryClient.invalidateQueries({ queryKey: ['cinema-films', venueId] });
      setShowModal(false);
      reset();
    },
  });

  const bulkCreateMutation = useMutation({
    mutationFn: async (data: BulkFormData) => {
      await api.post(`/admin/venues/${venueId}/cinema/showings/bulk`, {
        ...data,
        dates: bulkDates,
        times: bulkTimes,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cinema-showings', venueId] });
      queryClient.invalidateQueries({ queryKey: ['cinema-films', venueId] });
      setShowBulkModal(false);
      resetBulk();
      setBulkDates([]);
      setBulkTimes([]);
      alert(`Created ${bulkDates.length * bulkTimes.length} showings`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (showingId: string) => {
      await api.delete(`/admin/venues/${venueId}/cinema/showings/${showingId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cinema-showings', venueId] });
      queryClient.invalidateQueries({ queryKey: ['cinema-films', venueId] });
    },
  });

  const addBulkDate = () => {
    const dateInput = document.getElementById('bulk-date') as HTMLInputElement;
    if (dateInput.value && !bulkDates.includes(dateInput.value)) {
      setBulkDates([...bulkDates, dateInput.value].sort());
      dateInput.value = '';
    }
  };

  const addBulkTime = () => {
    const timeInput = document.getElementById('bulk-time') as HTMLInputElement;
    if (timeInput.value && !bulkTimes.includes(timeInput.value)) {
      setBulkTimes([...bulkTimes, timeInput.value].sort());
      timeInput.value = '';
    }
  };

  const formatTime = (time: string) => {
    return time.substring(0, 5);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  // Group showings by screen
  const showingsByScreen = screens?.map((screen) => ({
    screen,
    showings: showings?.filter((s) => s.screen_id === screen.id) || [],
  }));

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(routeFilmId ? `/venues/${venueId}/cinema/films` : `/venues/${venueId}/cinema`)}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Showings</h1>
          <p className="text-sm text-gray-500">Schedule and manage film screenings</p>
        </div>
        <button onClick={() => setShowBulkModal(true)} className="btn btn-secondary">
          <Calendar className="h-4 w-4 mr-2" />
          Bulk Add
        </button>
        <button onClick={() => setShowModal(true)} className="btn btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          Add Showing
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="input"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Screen</label>
            <select
              value={filterScreenId}
              onChange={(e) => setFilterScreenId(e.target.value)}
              className="input"
            >
              <option value="">All Screens</option>
              {screens?.map((screen) => (
                <option key={screen.id} value={screen.id}>
                  {screen.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1" />
          <div className="text-sm text-gray-500">
            {showings?.length || 0} showing{showings?.length !== 1 ? 's' : ''} on {formatDate(selectedDate)}
          </div>
        </div>
      </div>

      {/* Showings Grid */}
      <div className="space-y-6">
        {isLoading ? (
          <div className="card p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : showingsByScreen?.every((s) => s.showings.length === 0) ? (
          <div className="card p-12 text-center text-gray-500">
            No showings scheduled for {formatDate(selectedDate)}.
          </div>
        ) : (
          showingsByScreen
            ?.filter((s) => !filterScreenId || s.screen.id === filterScreenId)
            .map(({ screen, showings: screenShowings }) => (
              <div key={screen.id} className="card p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Monitor className="h-5 w-5 text-gray-400" />
                  <h3 className="font-semibold">{screen.name}</h3>
                  <span className="text-sm text-gray-500">
                    ({screenShowings.length} showing{screenShowings.length !== 1 ? 's' : ''})
                  </span>
                </div>

                {screenShowings.length === 0 ? (
                  <p className="text-gray-400 text-sm">No showings on this screen</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {screenShowings
                      .sort((a, b) => a.start_time.localeCompare(b.start_time))
                      .map((showing) => (
                        <div
                          key={showing.id}
                          className="border rounded-lg p-3 hover:bg-gray-50 relative group"
                        >
                          <div className="font-medium text-lg flex items-center gap-1">
                            <Clock className="h-4 w-4 text-gray-400" />
                            {formatTime(showing.start_time)}
                          </div>
                          <div className="text-sm text-gray-600 truncate">
                            {showing.film_title}
                          </div>
                          <div className="text-xs text-gray-500">
                            {showing.currency} {showing.base_price?.toFixed(2)}
                          </div>

                          <button
                            onClick={() => {
                              if (confirm('Delete this showing?')) {
                                deleteMutation.mutate(showing.id);
                              }
                            }}
                            className="absolute top-1 right-1 p-1 rounded hover:bg-red-100 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))
        )}
      </div>

      {/* Add Single Showing Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Add Showing</h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit((data) => createMutation.mutate(data))} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Film *</label>
                <select {...register('filmId')} className="input">
                  <option value="">Select film</option>
                  {films?.filter((f) => f.status !== 'ended').map((film) => (
                    <option key={film.id} value={film.id}>
                      {film.title}
                    </option>
                  ))}
                </select>
                {errors.filmId && <p className="text-sm text-red-600 mt-1">{errors.filmId.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Screen *</label>
                <select {...register('screenId')} className="input">
                  <option value="">Select screen</option>
                  {screens?.map((screen) => (
                    <option key={screen.id} value={screen.id}>
                      {screen.name}
                    </option>
                  ))}
                </select>
                {errors.screenId && <p className="text-sm text-red-600 mt-1">{errors.screenId.message}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input type="date" {...register('showDate')} className="input" />
                  {errors.showDate && <p className="text-sm text-red-600 mt-1">{errors.showDate.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time *</label>
                  <input type="time" {...register('startTime')} className="input" />
                  {errors.startTime && <p className="text-sm text-red-600 mt-1">{errors.startTime.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                  <input type="time" {...register('endTime')} className="input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Base Price</label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('basePrice', { valueAsNumber: true })}
                    className="input"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={createMutation.isPending} className="btn btn-primary">
                  {createMutation.isPending ? 'Creating...' : 'Create Showing'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Add Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Bulk Add Showings</h2>
              <button onClick={() => setShowBulkModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitBulk((data) => bulkCreateMutation.mutate(data))} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Film *</label>
                <select {...registerBulk('filmId')} className="input">
                  <option value="">Select film</option>
                  {films?.filter((f) => f.status !== 'ended').map((film) => (
                    <option key={film.id} value={film.id}>
                      {film.title}
                    </option>
                  ))}
                </select>
                {bulkErrors.filmId && <p className="text-sm text-red-600 mt-1">{bulkErrors.filmId.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Screen *</label>
                <select {...registerBulk('screenId')} className="input">
                  <option value="">Select screen</option>
                  {screens?.map((screen) => (
                    <option key={screen.id} value={screen.id}>
                      {screen.name}
                    </option>
                  ))}
                </select>
                {bulkErrors.screenId && <p className="text-sm text-red-600 mt-1">{bulkErrors.screenId.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Base Price</label>
                <input
                  type="number"
                  step="0.01"
                  {...registerBulk('basePrice', { valueAsNumber: true })}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dates ({bulkDates.length} selected)
                </label>
                <div className="flex gap-2">
                  <input type="date" id="bulk-date" className="input flex-1" />
                  <button type="button" onClick={addBulkDate} className="btn btn-secondary">
                    Add
                  </button>
                </div>
                {bulkDates.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {bulkDates.map((date) => (
                      <span
                        key={date}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm"
                      >
                        {formatDate(date)}
                        <button
                          type="button"
                          onClick={() => setBulkDates(bulkDates.filter((d) => d !== date))}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Times ({bulkTimes.length} selected)
                </label>
                <div className="flex gap-2">
                  <input type="time" id="bulk-time" className="input flex-1" />
                  <button type="button" onClick={addBulkTime} className="btn btn-secondary">
                    Add
                  </button>
                </div>
                {bulkTimes.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {bulkTimes.map((time) => (
                      <span
                        key={time}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm"
                      >
                        {time}
                        <button
                          type="button"
                          onClick={() => setBulkTimes(bulkTimes.filter((t) => t !== time))}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {bulkDates.length > 0 && bulkTimes.length > 0 && (
                <p className="text-sm text-gray-500">
                  This will create {bulkDates.length * bulkTimes.length} showings
                </p>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowBulkModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={bulkCreateMutation.isPending || bulkDates.length === 0 || bulkTimes.length === 0}
                  className="btn btn-primary"
                >
                  {bulkCreateMutation.isPending ? 'Creating...' : 'Create Showings'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
